// StripeAccountDetail — CHG-1642 / ITER-3752.
//
// Wave 1 Tier 1 v1→v2 port. Adapts web/dashboard/src/pages/
// StripeAccountDetail.tsx to the v2 DetailPage primitive. Hand-rolled
// inline config (no entity-hint generator wiring); documented exception
// per feedback_deterministic_codegen_over_hand_rolling.md — admin port,
// no existing entity-hint, and the page composes 4 tabs of which 2
// (payout history, disputes) are backend-pending placeholders so the
// generator's standard tab shape doesn't fit cleanly anyway.
//
// Brief asked for "payout history + dispute review + refund button".
// Today's backend at src/api/app/routers/stripe_accounts.py exposes
// only list / get / update / stats / capabilities — no payout
// or dispute resources, and refund operates on donation_id
// (POST /donations/{id}/refund) not stripe_account_id. The two
// placeholder tabs cite DRIFT-702 below; refund is intentionally NOT
// exposed on the account detail page because the v1 page never had
// it either and the proper UX is on donation detail.

import { useQuery } from "@tanstack/react-query";
import { DetailPage, type DetailPageConfig } from "../components/composition";
import {
  getStripeAccount,
  getStripeAccountCapabilities,
  type StripeAccount,
} from "../api/stripe-accounts";
import type { ReactNode } from "react";

const STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  active: "Active",
  restricted: "Restricted",
  rejected: "Rejected",
  disabled: "Disabled",
};

function row(label: string, value: ReactNode): ReactNode {
  return (
    <div
      key={label}
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "8px 0",
        borderBottom: "1px solid var(--color-border-subtle)",
        fontSize: 13,
      }}
    >
      <span style={{ color: "var(--color-text-muted)" }}>{label}</span>
      <span style={{ textAlign: "right" }}>{value}</span>
    </div>
  );
}

function CapabilitiesTab({ accountId }: { accountId: string }) {
  const q = useQuery({
    queryKey: ["stripe-account-capabilities", accountId] as const,
    queryFn: () => getStripeAccountCapabilities(accountId),
  });
  if (q.isLoading) {
    return <div className="card" style={{ padding: 16, fontSize: 13 }}>Loading capabilities…</div>;
  }
  if (q.error) {
    return (
      <div className="card" style={{ padding: 16, fontSize: 13, color: "var(--color-text-muted)" }}>
        Couldn't load capabilities. {(q.error as Error).message}
      </div>
    );
  }
  const caps = q.data?.capabilities ?? {};
  const keys = Object.keys(caps).sort();
  if (keys.length === 0) {
    return (
      <div className="card" style={{ padding: 16, fontSize: 13, color: "var(--color-text-muted)" }}>
        Stripe hasn't reported any capabilities for this account yet.
      </div>
    );
  }
  return (
    <div className="card" style={{ padding: 16 }}>
      {keys.map((k) => row(k, caps[k]))}
    </div>
  );
}

const STILL_BACKEND_PENDING_COPY =
  "This tab consumes a backend endpoint that isn't shipped yet. Filed as DRIFT-702 against the wave 1 Stripe port; the v1 dashboard didn't surface this either, so the gap predates the port.";

export function StripeAccountDetail() {
  const config: DetailPageConfig<StripeAccount> = {
    idParam: "id",
    backTo: "/stripe-accounts",
    queryKey: (id) => ["stripe-account", id] as const,
    queryFn: (id) => getStripeAccount(id),
    title: (r) => r.stripe_account_id || "Stripe Account",
    subtitle: (r) => r.support_email || "",
    statusCards: (r) => [
      {
        id: "charges",
        label: "Charges",
        value: r.charges_enabled ? "Enabled" : "Disabled",
        format: "text",
        tone: r.charges_enabled ? "g-giving" : "g-people",
      },
      {
        id: "payouts",
        label: "Payouts",
        value: r.payouts_enabled ? "Enabled" : "Disabled",
        format: "text",
        tone: r.payouts_enabled ? "g-giving" : "g-people",
      },
    ],
    tabs: [
      {
        id: "details",
        label: "Details",
        render: (r, fmt) => (
          <div className="card" style={{ padding: 16 }}>
            {row("Stripe Account ID", <span className="tabular">{r.stripe_account_id}</span>)}
            {row("Organization", r.organization_name ?? r.organization_id)}
            {row("Support Email", r.support_email || "—")}
            {row("Statement Descriptor", r.statement_descriptor ?? "—")}
            {row("Status", STATUS_LABEL[r.account_status] ?? r.account_status)}
            {row("Charges Enabled", r.charges_enabled ? "Yes" : "No")}
            {row("Payouts Enabled", r.payouts_enabled ? "Yes" : "No")}
            {row("Payout Schedule", r.payout_schedule)}
            {row("Updated", fmt.formatDate(r.updated_at, "medium"))}
          </div>
        ),
      },
      {
        id: "capabilities",
        label: "Capabilities",
        render: (r) => <CapabilitiesTab accountId={r.id} />,
      },
      {
        id: "payouts",
        label: "Payout history",
        render: () => (
          <div className="card" style={{ padding: 16, fontSize: 13, color: "var(--color-text-muted)" }}>
            <p style={{ margin: 0, marginBottom: 8 }}>
              <strong>Payout history — coming soon.</strong>
            </p>
            <p style={{ margin: 0 }}>{STILL_BACKEND_PENDING_COPY}</p>
          </div>
        ),
      },
      {
        id: "disputes",
        label: "Disputes",
        render: () => (
          <div className="card" style={{ padding: 16, fontSize: 13, color: "var(--color-text-muted)" }}>
            <p style={{ margin: 0, marginBottom: 8 }}>
              <strong>Disputes — coming soon.</strong>
            </p>
            <p style={{ margin: 0 }}>{STILL_BACKEND_PENDING_COPY}</p>
          </div>
        ),
      },
    ],
  };

  return <DetailPage config={config} />;
}
